import { useEffect, useMemo, useRef } from "react"
import { AxiosInstance, AxiosResponse, isAxiosError } from "axios"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import {
  fetchAsyncResult,
  InitialiseDataReporting,
  pollAsync,
  RunQuery,
} from "api/Engine/data-reporting"
import type { QueryInstance } from "types/engine.types"
import type { InitialiseMode } from "types/runOnLoad.types"
import { ENGINE_TIMEOUT_MS, EngineTimeoutError } from "./engineTimeout"

export interface EngineCommands {
  initialiseData: (projectId: string, onload?: InitialiseMode) => Promise<AxiosResponse<unknown>>
  RunEngineQuery: (
    instance: QueryInstance,
    script: string,
    isAsync: boolean
  ) => Promise<AxiosResponse<unknown>>
  pollingAsync: (asyncId: string) => Promise<AxiosResponse<unknown>>
  getAsyncResult: (asyncId: string) => Promise<AxiosResponse<unknown>>
}

/**
 * The body of `/async-get` as the queue reads it: an object. Depending on the
 * transport the body arrives already parsed or as raw text (the shared
 * `api/asyncJob.fetchAsyncResult` asks for text, see `hooks/core/useAsync`),
 * so text is read back here; an object passes through untouched. A body that
 * cannot be read fails with the job id instead of reaching
 * `extractEngineError` as a string ("Empty engine response").
 */
export const parseAsyncResultBody = (data: unknown, asyncId: string): unknown => {
  if (typeof data !== "string") return data
  try {
    return JSON.parse(data)
  } catch {
    throw new Error(`The result of job ${asyncId} came back in a form this app could not read.`)
  }
}

/** Axios stopped the request for its `timeout` (not an abort, which shares the code). */
const timedOut = (e: unknown): boolean =>
  isAxiosError(e) &&
  (e.code === "ETIMEDOUT" || (e.code === "ECONNABORTED" && /timeout/i.test(e.message)))

/**
 * Sends one request within `ENGINE_TIMEOUT_MS`, counted from before the
 * authed axios is fetched, so a token that never comes is covered too. The
 * axios `timeout` stops the request itself; the timer also ends the wait for
 * a request that does not honour it. Either way the error is an
 * `EngineTimeoutError`.
 */
const inTime = async <T>(
  authed: () => Promise<AxiosInstance>,
  send: (axios: AxiosInstance) => Promise<T>
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  const late = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new EngineTimeoutError()), ENGINE_TIMEOUT_MS)
  })
  try {
    const axios = await Promise.race([authed(), late])
    // Every call of the getter builds a new instance, so this default is this request's alone.
    axios.defaults.timeout = ENGINE_TIMEOUT_MS
    return await Promise.race([send(axios), late])
  } catch (e) {
    throw timedOut(e) ? new EngineTimeoutError() : e
  } finally {
    clearTimeout(timer)
  }
}

/**
 * The engine requests of the tab spec, as plain calls under the same names
 * as `useEngine` and `useAsync`. Those run each request as a react-query
 * mutation whose pending state re-renders the component holding the hook
 * when the request starts and when it ends; the tab spec never shows that
 * state, and its screen holds the session and the queue, so every request
 * of the preview or the tab book rendered the whole screen. These calls hold
 * no state, so a request renders nothing by itself. Errors reach the caller
 * as before.
 *
 * The commands keep their identity for the life of the component, as the
 * mutations' did: each request takes the latest authed axios when it is
 * sent. The session's queue is built from them, so a new one would end the
 * queue of a session that is still running.
 *
 * With `timeout` (the queue's commands), every request may take 5 minutes
 * and one that takes longer fails with an `EngineTimeoutError`, so a table
 * or a tab book never waits for ever on a request that does not settle.
 * Without it (the session's commands) a request takes as long as it takes.
 */
export const useEngineCommands = ({
  timeout = false,
}: { timeout?: boolean } = {}): EngineCommands => {
  const getAxios = useAuthedAxios()
  const latest = useRef(getAxios)
  useEffect(() => {
    latest.current = getAxios
  }, [getAxios])
  return useMemo((): EngineCommands => {
    const authed = () => latest.current()
    const send = <T>(request: (axios: AxiosInstance) => Promise<T>): Promise<T> =>
      timeout ? inTime(authed, request) : authed().then(request)
    return {
      initialiseData: (projectId, onload) =>
        send((axios) => InitialiseDataReporting(axios, projectId, onload)),
      RunEngineQuery: (instance, script, isAsync) =>
        send((axios) => RunQuery(axios, instance, script, isAsync)),
      pollingAsync: (asyncId) => send((axios) => pollAsync(axios, asyncId)),
      getAsyncResult: (asyncId) =>
        send(async (axios) => {
          const response = await fetchAsyncResult(axios, asyncId)
          return { ...response, data: parseAsyncResultBody(response.data, asyncId) }
        }),
    }
  }, [timeout])
}
